/*
 * Branded share card template — settings' `share_card` contract v4
 * (REFERRAL-BUILD-SPLIT-V1 §1 item 10; canon draft 11b; template
 * `referral-card-v1`). This module is the ONE place the contract is read.
 *
 * resolveShareCard(code) →
 *   { template, plain:false }  settings sent a valid referral-card-v1 template:
 *                              every rectangle, colour and string comes from it.
 *   { template, plain:true }   share_card missing, malformed or an UNKNOWN
 *                              template_version: a plain card with no images
 *                              (solid colour + wordmark + code + link), built
 *                              from built-in layout constants that mirror v1.
 *                              The label is the neutral "USE MY CODE" (true at
 *                              any discount; never a %, never the prize).
 *
 * The prize never appears on the card: no field of this template carries it.
 */
import type { ReferralCode } from '../data/referralCodes'

export const SHARE_CARD_TEMPLATE_VERSION = 'referral-card-v1'

export interface Rect { x: number; y: number; w: number; h: number }

export interface ShareCardLayout {
  w: number
  h: number
  top: Rect
  panel: { y: number; h: number }
  message: Rect & { max_lines: number }
  code: Rect
  link: Rect
  /** Stories only: the app's own UI covers y < top and y > bottom. */
  platform_ui_top?: number
  platform_ui_bottom?: number
}

export interface ShareCardColours { panel_bg: string; panel_text: string; accent: string }

export interface ShareCardTemplateV1 {
  template_version: typeof SHARE_CARD_TEMPLATE_VERSION
  event_name: string
  event_date_display: string | null
  venue_display: string | null
  logo_url: string | null
  logo_on_dark_url: string | null
  hero_image_url: string | null
  colours: ShareCardColours
  code: string
  code_label: string
  short_link: string
  default_message: string
  layouts: { square: ShareCardLayout; story: ShareCardLayout }
}

export type ShareCardSize = 'square' | 'story'

/** Built-in layouts = the v1 contract's published constants. Used only for
 *  the plain card (settings' own layouts win whenever the template is valid). */
export const DEFAULT_LAYOUTS: ShareCardTemplateV1['layouts'] = {
  square: {
    w: 1080, h: 1080,
    top: { x: 64, y: 56, w: 952, h: 250 },
    panel: { y: 690, h: 390 },
    message: { x: 64, y: 720, w: 952, h: 110, max_lines: 2 },
    code: { x: 64, y: 850, w: 952, h: 150 },
    link: { x: 64, y: 1010, w: 952, h: 46 },
  },
  story: {
    w: 1080, h: 1920, platform_ui_top: 250, platform_ui_bottom: 1620,
    top: { x: 64, y: 250, w: 952, h: 280 },
    panel: { y: 1150, h: 470 },
    message: { x: 64, y: 1185, w: 952, h: 120, max_lines: 2 },
    code: { x: 64, y: 1330, w: 952, h: 170 },
    link: { x: 64, y: 1520, w: 952, h: 60 },
  },
}

export const DEFAULT_COLOURS: ShareCardColours = { panel_bg: '#14213d', panel_text: '#ffffff', accent: '#f2b84b' }
export const PLAIN_CODE_LABEL = 'USE MY CODE'

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null)
const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i
const imageUrl = (v: unknown): string | null => (typeof v === 'string' && /^https:\/\/\S+$/i.test(v) ? v : null)

function rect(v: unknown, W: number, H: number): Rect | null {
  if (!isObject(v) || !num(v.x) || !num(v.y) || !num(v.w) || !num(v.h)) return null
  if (v.x < 0 || v.y < 0 || v.w <= 0 || v.h <= 0 || v.x + v.w > W || v.y + v.h > H) return null
  return { x: v.x, y: v.y, w: v.w, h: v.h }
}

function layout(v: unknown, expectW: number, expectH: number): ShareCardLayout | null {
  if (!isObject(v) || v.w !== expectW || v.h !== expectH) return null
  const W = expectW
  const H = expectH
  const top = rect(v.top, W, H)
  const code = rect(v.code, W, H)
  const link = rect(v.link, W, H)
  const msg = rect(v.message, W, H)
  const p = v.panel
  if (!top || !code || !link || !msg || !isObject(p) || !num(p.y) || !num(p.h) || p.y < 0 || p.y + p.h > H) return null
  const maxLines = isObject(v.message) && num(v.message.max_lines) && v.message.max_lines >= 1 ? Math.floor(v.message.max_lines) : 2
  const out: ShareCardLayout = { w: W, h: H, top, panel: { y: p.y, h: p.h }, message: { ...msg, max_lines: maxLines }, code, link }
  if (num(v.platform_ui_top)) out.platform_ui_top = v.platform_ui_top
  if (num(v.platform_ui_bottom)) out.platform_ui_bottom = v.platform_ui_bottom
  return out
}

/** Strict parse of settings' share_card. null ⇒ unknown version or malformed. */
export function parseShareCardTemplate(raw: unknown): ShareCardTemplateV1 | null {
  if (!isObject(raw) || raw.template_version !== SHARE_CARD_TEMPLATE_VERSION) return null
  const eventName = str(raw.event_name)
  const code = str(raw.code)
  const codeLabel = str(raw.code_label)
  const shortLink = str(raw.short_link)
  if (!eventName || !code || !codeLabel || !shortLink) return null
  const c = raw.colours
  if (!isObject(c) || typeof c.panel_bg !== 'string' || !HEX.test(c.panel_bg) || typeof c.panel_text !== 'string' || !HEX.test(c.panel_text) || typeof c.accent !== 'string' || !HEX.test(c.accent)) return null
  const L = raw.layouts
  if (!isObject(L)) return null
  const square = layout(L.square, 1080, 1080)
  const story = layout(L.story, 1080, 1920)
  if (!square || !story) return null
  return {
    template_version: SHARE_CARD_TEMPLATE_VERSION,
    event_name: eventName,
    event_date_display: str(raw.event_date_display),
    venue_display: str(raw.venue_display),
    logo_url: imageUrl(raw.logo_url),
    logo_on_dark_url: imageUrl(raw.logo_on_dark_url),
    hero_image_url: imageUrl(raw.hero_image_url),
    colours: { panel_bg: c.panel_bg, panel_text: c.panel_text, accent: c.accent },
    code,
    code_label: codeLabel,
    short_link: shortLink,
    default_message: typeof raw.default_message === 'string' ? raw.default_message : '',
    layouts: { square, story },
  }
}

/** The share page URL as displayed on the card: no scheme (contract rule for short_link). */
export const displayLink = (url: string): string => url.replace(/^https?:\/\//i, '').replace(/\/$/, '')

export interface ResolvedShareCard {
  template: ShareCardTemplateV1
  /** true ⇒ settings' template was missing / unknown: no images, built-in layout. */
  plain: boolean
}

export function resolveShareCard(code: ReferralCode): ResolvedShareCard {
  const t = parseShareCardTemplate(code.share_card)
  if (t && t.code === code.code) return { template: t, plain: false }
  return {
    plain: true,
    template: {
      template_version: SHARE_CARD_TEMPLATE_VERSION,
      event_name: code.event_name,
      event_date_display: null,
      venue_display: null,
      logo_url: null,
      logo_on_dark_url: null,
      hero_image_url: null,
      colours: DEFAULT_COLOURS,
      code: code.code,
      code_label: PLAIN_CODE_LABEL,
      short_link: displayLink(code.share_page_url),
      default_message: code.copy.share_text ?? '',
      layouts: DEFAULT_LAYOUTS,
    },
  }
}
